"use client";

import { cn, extractKeyTextValue, formatPrice, getMedusaProductPrice, isSaleProduct } from "@/lib/utils";
import { useEffect, useState } from "react";
import ImageGallery from "@/components/molecules/image-gallery";
import ModalTemplate from "../molecules/modal-template";
import AddToFav from "@/modules/add-to-fav/add-to-fav";
import { ArrowUpRight, HeartIcon, Loader2, Minus, Plus, ShoppingCart } from "lucide-react";
import Link from "next/link";
import { Button, buttonVariants } from "../atoms/buttons";
import { toast, Toast } from "../atoms/toast";
import { PrismicRichText } from "@prismicio/react";
import { type ProductImage as ProductImageType } from "@/types";
import { CategorieDocument, ProductDocument } from "@/prismicio-types";
import { Locales } from "@/lib/i18n";
import { useProduct, useProducts } from "@/hooks/useProduct";
import { __, _x, getPermalink } from "@/lib/i18n/functions";
import { useAddToCart, useRemoveCartItem } from "@/hooks/useCartMutations";
import { useCart } from "@/hooks/useCart";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import ProductCollection from "@/components/molecules/product-collection";
import { useWishlist } from "@/hooks/useWishlist";
import ProductRange from "@/components/organisms/product-range";
import { StoreProduct } from "@medusajs/types";
import { Badge } from "@/components/ui/badge";
import { useProductVariant } from "@/hooks/useProductVariant";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toLotQuantity,
  toPieceQuantity,
  normalizePieceQuantity,
  resolveProductConditioning,
  toPiecePrice,
} from "@/lib/product-units";
import {
  isProductOrderable,
  resolveProductAvailability,
} from "@/lib/product-availability";

type ProductHeroProps = {
  productInfo: {
    prismicProduct: ProductDocument;
    processedImages: ProductImageType[];
    parentCollection: ProductDocument | undefined;
    medusaProduct: StoreProduct | undefined;
  };
  isRange?: boolean;
  className?: string;
  isOutOfStock?: boolean;
  isCollection?: boolean;
  collectionProducts: ProductDocument[] | null;
  collectionCategories: CategorieDocument[][] | null;
  locale: Locales;
  isImageFullWidth?: boolean;
  onlyDisplayFirstImage?: boolean;
};

const ProductHero = ({
  productInfo,
  isRange,
  isCollection,
  className,
  isOutOfStock,
  collectionProducts,
  locale,
  isImageFullWidth = false,
  onlyDisplayFirstImage = false,
}: ProductHeroProps) => {
  const [count, setCount] = useState(1);
  const [inputValue, setInputValue] = useState("1");
  const [isEditing, setIsEditing] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const medusaId = extractKeyTextValue(productInfo.prismicProduct.data.medusaId);

  const { lists, isProductInAnyList, isLoading: isWishlistLoading } = useWishlist();
  const isWishlisted = isProductInAnyList(medusaId);
  const [showListDropdown, setShowListDropdown] = useState(isWishlisted);

  const { parentCollection } = productInfo;
  const collectionMedusaIds = collectionProducts?.map((product) => extractKeyTextValue(product.data.medusaId)) || [];

  const {
    data: medusaData,
    isLoading: isSingleLoading,
    isFetching: isSingleFetching,
    isError: isSingleError,
  } = useProduct(locale, medusaId, !isCollection);
  const { data: medusaProducts, isLoading: isBatchLoading } = useProducts(
    locale,
    [...collectionMedusaIds],
    isCollection
  );

  const isLoading = isSingleLoading || isSingleFetching || isBatchLoading;
  const availability = resolveProductAvailability({
    product: medusaData,
    isLoading,
    isError: isSingleError,
  });
  const isCurrentlyUnavailable =
    !isCollection &&
    (availability.kind === "unavailable" ||
      (availability.kind === "loading" && Boolean(isOutOfStock)));
  const hasAvailabilityError =
    !isCollection && availability.kind === "technical_error";

  const productConditioning = resolveProductConditioning(
    medusaData?.metadata?.conditioning,
    medusaData?.metadata?.conditioning_bac
  );

  // seedFromUrl: true → on PDP the URL param is the first-visit seed
  const { hasMultipleVariants, resolvedSurface, selectedVariant, selectedVariantPrice, setVariant, hasCustomization } =
    useProductVariant(productInfo.medusaProduct, medusaId, { seedFromUrl: true });

  // When this is a collection page, read which sub-product is selected from the URL
  const { cart } = useCart();

  // Both single and multi-variant: selectedVariant is always the right variant
  const cartItem = cart?.items?.find((item) => item.variant_id === selectedVariant?.id);
  const isInCart = Boolean(cartItem);
  const cartQuantity = toLotQuantity(cartItem?.quantity || 0, productConditioning).toString();

  // Used to remove the current cart item before adding (PDP replace-not-increment behaviour)
  const existingCartItemForProduct = cart?.items?.find((item) =>
    productInfo.medusaProduct?.variants?.some((variant) => variant.id === item.variant_id)
  );

  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      toast.success({ title: __("Item added to cart!", locale) });
      // If the surface only lived in the URL (first visit, store was empty),
      // persist it to the store now so other pages (wishlist) can read it.
      if (hasMultipleVariants && resolvedSurface) {
        setVariant(resolvedSurface);
      }
    },
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });
  const { removeItem } = useRemoveCartItem();

  useEffect(() => {
    if (isWishlisted) setShowListDropdown(true);
  }, [isWishlisted]);

  useEffect(() => {
    if (!isEditing) setInputValue(toLotQuantity(count, productConditioning).toString());
  }, [count, isEditing, productConditioning]);

  const incrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCount((c) => c + quantityStep);
  };

  const decrementCount = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCount((c) => (c > quantityStep ? c - quantityStep : c));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "" || /^\d+$/.test(val)) setInputValue(val);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const clamped = toPieceQuantity(parseInt(inputValue) || 1, productConditioning);
    setCount(clamped);
    setInputValue(toLotQuantity(clamped, productConditioning).toString());
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setInputValue(toLotQuantity(count, productConditioning).toString());
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    setTimeout(() => {
      (document.activeElement as HTMLInputElement)?.select();
    }, 0);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSingleError) {
      toast.error({ title: __("Unable to verify product availability", locale) });
      return;
    }

    if (!isProductOrderable(medusaData)) {
      toast.error({ title: __("This product is no longer available", locale) });
      return;
    }

    // Range product with no customisation at all (single-variant range)
    if (isRange && !hasCustomization && !hasMultipleVariants) {
      toast.error({ title: __("Please customize this product before adding to cart.", locale) });
      return;
    }

    // PDP behaviour: replace (remove existing) rather than increment
    if (existingCartItemForProduct) {
      removeItem(existingCartItemForProduct.id);
    }

    if (hasMultipleVariants && selectedVariant) {
      const freshVariant = medusaData?.variants?.find(
        (variant) => variant.id === selectedVariant.id
      );
      if (!freshVariant?.id) {
        toast.error({ title: __("This product is no longer available", locale) });
        return;
      }
      addToCart(freshVariant.id, count);
      return;
    }

    // Single variant — use freshly fetched medusaData so we always have the
    // latest pricing; variant IDs are stable so this is safe.
    const variantId = medusaData?.variants?.[0]?.id;
    if (!variantId) {
      console.error("Variant ID is missing", medusaData);
      return;
    }
    addToCart(variantId, count);
  };

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isWishlisted) {
      setOpenModal(true);
    } else {
      setShowListDropdown(true);
    }
  };

  const physicalPieceCount = normalizeConditioning(productConditioning);
  const quantityStep = getCartQuantityStep(productConditioning);
  useEffect(() => {
    setCount((current) => normalizePieceQuantity(current, productConditioning));
  }, [productConditioning]);
  const medusaProductPrice = medusaData?.variants?.[0]?.calculated_price?.calculated_amount || 0;
  const pricePerPiece = toPiecePrice(medusaProductPrice, productConditioning);

  const height = productInfo?.medusaProduct?.variants?.[0]?.height || medusaData?.variants?.[0]?.height;
  const length = productInfo?.medusaProduct?.variants?.[0]?.length || medusaData?.variants?.[0]?.length;
  const width = productInfo?.medusaProduct?.variants?.[0]?.width || medusaData?.variants?.[0]?.width;
  const diameter =
    (productInfo?.medusaProduct?.metadata?.diameter as number) || (medusaData?.metadata?.diameter as number);
  const hasDimensionValue = (value: unknown) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "number") return value > 0;
    if (typeof value === "string") {
      const trimmedValue = value.trim();
      if (!trimmedValue) return false;

      const numericValue = Number.parseFloat(trimmedValue.replace(",", "."));
      return Number.isFinite(numericValue) ? numericValue > 0 : true;
    }

    return Boolean(value);
  };
  const hasHeight = hasDimensionValue(height);
  const hasLength = hasDimensionValue(length);
  const hasWidth = hasDimensionValue(width);
  const hasDiameter = hasDimensionValue(diameter);
  const hasAnyDimensions = hasHeight || hasLength || hasWidth || hasDiameter;
  const dimensions = [
    hasHeight && { label: __("Height", locale), value: height },
    hasLength && { label: __("Length", locale), value: length },
    hasWidth && { label: __("Width", locale), value: width },
    hasDiameter && { label: __("Diameter", locale), value: diameter },
  ].filter(Boolean) as { label: string; value: unknown }[];
  const isPriceDynamic = productInfo?.medusaProduct?.metadata?.is_price_dynamic as boolean;
  const notice =
    (productInfo?.medusaProduct?.metadata?.[`notice_${locale}`] as string | undefined) ??
    (medusaData?.metadata?.[`notice_${locale}`] as string | undefined) ??
    __("This product requires a custom price. Festi will send you information after your quote.", locale);

  const price = getMedusaProductPrice(medusaData ?? undefined);

  const dimensionsBlock = hasAnyDimensions ? (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {dimensions.map((dimension) => (
        <div key={dimension.label} className="text-primary-light flex items-baseline gap-1.5 text-sm">
          <span className="font-medium">{dimension.label}</span>
          <span>
            {String(dimension.value)} <sup aria-hidden="true">{__("cm", locale)}</sup>
          </span>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <section
      className={cn(
        "container grid grid-cols-1 gap-4 px-4 pt-4 md:gap-6 md:px-7.5 md:pt-7.5 lg:grid-cols-2",
        className
      )}
    >
      <ImageGallery
        locale={locale}
        images={productInfo.processedImages}
        className="max-w-none max-md:h-96 sm:max-w-none lg:max-w-[761px]"
        isImageFullWidth={isImageFullWidth}
        onlyDisplayFirstImage={onlyDisplayFirstImage}
        collectionProducts={collectionProducts}
        medusaProducts={medusaProducts}
      />

      <article className="flex flex-col gap-4">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex gap-2 max-sm:flex-col-reverse sm:items-center">
            <h2 className="font-cabinet-grotesk text-[26px] font-medium">{productInfo.prismicProduct.data?.title}</h2>
            {isSaleProduct(productInfo.medusaProduct) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
          </div>

          {!isCollection &&
            (showListDropdown ? (
              <ModalTemplate
                title={__("Please select your list", locale)}
                className="w-full sm:max-w-[506px]"
                classNameTitle="text-lg font-normal"
                triggerAsChild
                modalTrigger={
                  <button
                    className="flex cursor-pointer items-center justify-center gap-2 rounded border px-3 py-1.5 md:flex-row-reverse md:justify-start"
                    aria-label={__("Select wishlist for this product", locale)}
                  >
                    <HeartIcon className="fill-pink-primary stroke-pink-primary size-6 md:size-7" aria-hidden="true" />
                    <span className="text-sm">{__("Select a list", locale)}</span>
                  </button>
                }
              >
                <AddToFav productId={medusaId} locale={locale} />
              </ModalTemplate>
            ) : (
              <>
                {isLoading || isWishlistLoading ? (
                  <div className="shimmer h-10 w-full rounded bg-[#eaeaea]! opacity-50 md:h-8 md:w-8" />
                ) : (
                  <button
                    onClick={handleWishlistClick}
                    className="group flex cursor-pointer items-center justify-center gap-2 rounded border px-0 py-1.5 transition-colors md:flex-row-reverse md:justify-start md:border-0"
                    aria-label={__("Add to wishlist", locale)}
                  >
                    <HeartIcon
                      strokeWidth={1.5}
                      aria-hidden="true"
                      className="text-dark-primary group-hover:text-pink-primary group-hover:fill-pink-primary size-6 md:size-7"
                    />
                    <span className="text-sm md:hidden">{__("Select a list", locale)}</span>
                  </button>
                )}
              </>
            ))}
        </div>

        {openModal && (
          <ModalTemplate
            title={__("Please select your list", locale)}
            className="w-full sm:max-w-[506px]"
            classNameTitle="text-lg font-normal"
            triggerAsChild={false}
            open={openModal}
            onOpenChange={(open) => setOpenModal(open)}
            modalTrigger={""}
          >
            <AddToFav locale={locale} productId={medusaId} showForm={!lists?.length} />
          </ModalTemplate>
        )}

        {!isRange &&
          !isPriceDynamic &&
          !isCollection &&
          !isCurrentlyUnavailable &&
          !hasAvailabilityError &&
          parentCollection !== undefined && (
          <Link
            href={`${getPermalink(locale, "product")}/${parentCollection.uid}`}
            className={cn(
              buttonVariants({ variant: "secondary" }),
              "flex w-fit items-center gap-2.5 rounded border p-2.5 text-sm sm:hidden"
            )}
          >
            {parentCollection.data.title} <ArrowUpRight />
          </Link>
        )}

        {!isCollection && (
          <dl
            className={cn(
              "flex items-center justify-between gap-3 max-sm:hidden",
              (isRange || isPriceDynamic) && "border-b pb-3"
            )}
          >
            <div
              className={cn(
                "flex w-full items-center justify-between gap-1.5",
                (isRange || isPriceDynamic) && "items-end",
                hasMultipleVariants && "items-center border-t pt-3"
              )}
            >
              <div className="flex w-fit items-center gap-1.5">
                {/* Standard single-variant price */}
                {!isRange && !isPriceDynamic && !hasMultipleVariants && (
                  <>
                    <dd className="sr-only">{__("Product Price", locale)}</dd>
                    <dt className="font-cabinet-grotesk flex items-center gap-1.5 text-xl text-gray-900 md:text-2xl md:font-bold">
                      {isLoading ? getMedusaProductPrice(productInfo.medusaProduct) : price}
                    </dt>
                  </>
                )}

                {/* Price-on-demand notice for single-variant range/dynamic products */}
                {(isRange || isPriceDynamic) && !hasMultipleVariants && (
                  <Toast
                    id="product-hero"
                    type="warning"
                    className="mt-auto max-w-none py-3 shadow-none"
                    title={__("Price on demand", locale)}
                    description={notice}
                  />
                )}

                {/* Multi-variant: always show the currently selected variant price */}
                {hasMultipleVariants && (
                  <p className="font-cabinet-grotesk text-xl text-gray-900 md:text-2xl md:font-bold">
                    {selectedVariantPrice}
                  </p>
                )}

                {physicalPieceCount > 1 && (
                  <>
                    <dd className="sr-only">{__("Price per piece", locale)}</dd>
                    <dt className="text-primary-light mb-px text-sm">
                      /{physicalPieceCount} {__("pieces", locale)}
                    </dt>
                  </>
                )}
              </div>

              <div className="flex items-center gap-3">
                {physicalPieceCount > 1 && !isLoading && pricePerPiece !== 0 && (
                  <div>
                    <dd className="sr-only">{__("Price per piece", locale)}</dd>
                    <dt className="text-primary-light text-sm">
                      {formatPrice(pricePerPiece, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {__("/unit", locale)}
                    </dt>
                  </div>
                )}

                {(isRange || isPriceDynamic) && (
                  <div className="flex items-center gap-3.5">
                    <dd className="sr-only">{__("Product Reference", locale)}</dd>
                    <dt className={cn("text-primary-light text-sm", hasMultipleVariants && "text-base")}>
                      {__("REF", locale)} :{" "}
                      {productInfo.medusaProduct?.variants?.at(0)?.sku || medusaData?.variants?.[0].sku}
                    </dt>
                  </div>
                )}
              </div>
            </div>
          </dl>
        )}

        {/* ── Desktop: dimensions + ref ──────────────────────────────────── */}
        {!isRange && !isCollection && !isPriceDynamic && (
          <dl className="flex flex-wrap items-center justify-between gap-3 border-y py-3 max-sm:hidden">
            {dimensionsBlock}
            <div className="ml-auto flex items-center gap-3">
              <dd className="sr-only">{__("Product Reference", locale)}</dd>
              <dt className="text-primary-light">
                {__("REF", locale)} : {productInfo.medusaProduct?.variants?.at(0)?.sku || medusaData?.variants?.[0].sku}
              </dt>
              {parentCollection !== undefined && (
                <Link
                  href={`${getPermalink(locale, "product")}/${parentCollection.uid}`}
                  className={cn(
                    buttonVariants({ variant: "secondary" }),
                    "flex items-center gap-2.5 rounded border p-2.5 text-sm"
                  )}
                >
                  {parentCollection.data.title} <ArrowUpRight />
                </Link>
              )}
            </div>
          </dl>
        )}

        {/* ── Description + mobile dimensions ───────────────────────────── */}
        {!isCollection && (
          <div className={cn("flex flex-col gap-4", isRange && "mt-auto")}>
            <div className="text-sm leading-[130%] text-[#5D5D5D]">
              <PrismicRichText field={productInfo.prismicProduct.data?.description} />
            </div>
            {!!hasAnyDimensions && <div className="flex items-center gap-4 sm:hidden">{dimensionsBlock}</div>}
          </div>
        )}

        {/* ── Range configurator ────────────────────────────────────────── */}
        {isRange && !isCollection && (
          <ProductRange
            medusaProduct={productInfo?.medusaProduct}
            locale={locale}
            medusaId={productInfo?.medusaProduct?.id || ""}
            seedFromUrl={true}
          />
        )}

        {/* ── Collection ────────────────────────────────────────────────── */}
        {isCollection && (
          <ProductCollection
            isLoading={isLoading}
            medusaProducts={medusaProducts}
            collectionProducts={collectionProducts}
            locale={locale}
          />
        )}

        {/* ── Mobile: price + add-to-cart ───────────────────────────────── */}
        {!isCollection && (
          <>
            <dl
              className={cn(
                "flex items-center justify-between gap-3 border-t pt-3 sm:hidden",
                productConditioning === 1 && "pt-6"
              )}
            >
              <div className="flex w-full items-center justify-between gap-1.5">
                {/* Standard / multi-variant price */}
                {((!isRange && !isPriceDynamic) || hasMultipleVariants) && (
                  <>
                    <dd className="sr-only">{__("Product Price", locale)}</dd>
                    <dt className="font-cabinet-grotesk flex items-center gap-1.5 text-xl leading-0 text-gray-900 md:text-2xl md:font-bold">
                      {hasMultipleVariants ? (
                        <div>{selectedVariantPrice}</div>
                      ) : (
                        <div>
                          {price}{" "}
                          {physicalPieceCount > 1 && (
                            <span className="text-primary-light text-sm">
                              /{physicalPieceCount} {__("pieces", locale)}
                            </span>
                          )}
                        </div>
                      )}
                    </dt>
                  </>
                )}

                {/* Price-on-demand notice (single-variant) */}
                {(isRange || isPriceDynamic) && !hasMultipleVariants && (
                  <Toast
                    id="product-hero"
                    type="warning"
                    className="mt-auto max-w-none py-3 shadow-none"
                    title={__("Price on demand", locale)}
                    description={notice}
                  />
                )}

                {pricePerPiece !== medusaProductPrice && pricePerPiece !== 0 && (
                  <>
                    <dd className="sr-only">{__("Price per piece", locale)}</dd>
                    <dt className="text-primary-light text-sm">
                      {formatPrice(pricePerPiece, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      {__("/unit", locale)}
                    </dt>
                  </>
                )}
              </div>
            </dl>

            {/* ── In-cart state ── */}
            {isInCart ? (
              <div className="mt-auto flex items-center gap-3 sm:border-t sm:pt-4">
                <div className="flex flex-1 items-center gap-1">
                  <ShoppingCart className="text-green-600 md:size-5" aria-hidden="true" />
                  <span
                    className={cn(
                      "text-base font-medium text-green-600 md:text-xl",
                      locale === "nl" && "max-sm:hidden"
                    )}
                  >
                    {_x("In cart ({{ cartQuantity }})", { cartQuantity }, locale)}
                  </span>
                  {locale === "nl" && <span className="sm:hidden">{cartQuantity}</span>}
                </div>
                <Link
                  href={getPermalink(locale, "cart")}
                  aria-label={__("Go to cart", locale)}
                  className={cn(
                    buttonVariants(),
                    "h-11 flex-1 gap-3 text-base transition-transform duration-150 ease-in-out active:scale-95 md:h-14 md:text-lg"
                  )}
                >
                  {__("View Cart", locale)}
                </Link>
              </div>
            ) : (
              !isCurrentlyUnavailable &&
              !hasAvailabilityError && (
                <div
                  className={cn(
                    "flex w-full flex-col items-center justify-between gap-3 pb-1.5 sm:border-t sm:pt-4 md:flex-row md:gap-5",
                    isRange ? "" : "mt-auto"
                  )}
                >
                  {/* Quantity selector */}
                  <div className="flex h-11 w-full flex-1 items-center justify-between gap-1.25 rounded-sm md:h-14 md:w-[50%]">
                    <button
                      onClick={decrementCount}
                      className="group flex size-[30px] cursor-pointer items-center justify-center rounded-full border"
                      aria-label={__("Decrease quantity", locale)}
                    >
                      <Minus
                        size={14}
                        className={cn(
                          count <= quantityStep ? "text-primary-light/50" : "text-primary",
                          "group-focus:text-dark-primary"
                        )}
                        aria-hidden="true"
                      />
                    </button>
                    <div className="flex h-full min-w-[50px] grow items-center justify-center rounded-md border">
                      <input
                        size={1}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        onFocus={handleInputFocus}
                        aria-live="polite"
                        aria-atomic="true"
                        aria-label={__("Quantity", locale)}
                        className="w-full border-none bg-transparent text-center font-semibold outline-none focus:ring-0 md:text-base"
                        min={1}
                      />
                    </div>
                    <button
                      onClick={incrementCount}
                      className="group flex size-[30px] cursor-pointer items-center justify-center rounded-full border"
                      aria-label={__("Increase quantity", locale)}
                    >
                      <Plus
                        size={14}
                        className={cn(
                          count >= quantityStep * 10 ? "text-primary-light/50" : "text-primary",
                          "group-focus:text-dark-primary"
                        )}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  {/* Add to cart */}
                  <Button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart || isLoading}
                    aria-label={_x("Add {{ quantity }} of {{ productName }} to cart", {
                      quantity: formatProductQuantity(count, productConditioning, locale),
                      productName: productInfo.prismicProduct.data?.title || "",
                    }, locale)}
                    className="min-h-11 w-full gap-3 text-sm transition-transform duration-150 ease-in-out active:scale-95 sm:min-w-80 md:min-h-14 md:w-[60%] xl:text-base"
                  >
                    {isAddingToCart ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <>
                        <ShoppingCart size={20} />
                        <span className={cn("max-sm:hidden", locale === "nl" && "hidden")}>
                          {__("Add to cart", locale)}
                        </span>
                        <span className={cn("sm:hidden", locale === "nl" && "sm:block")}>{__("Add", locale)}</span>
                        <span className="tabular-nums whitespace-normal">
                          {formatProductQuantity(count, productConditioning, locale)}
                        </span>
                      </>
                    )}
                  </Button>
                </div>
              )
            )}
          </>
        )}

        {/* ── Out-of-stock notice ───────────────────────────────────────── */}
        {isCurrentlyUnavailable && (
          <Toast
            id="product-hero"
            type="info"
            className="mt-auto max-w-none shadow-none"
            title={__("This product is no longer available", locale)}
          />
        )}

        {hasAvailabilityError && (
          <Toast
            id="product-availability-error"
            type="error"
            className="mt-auto max-w-none shadow-none"
            title={__("Unable to verify product availability", locale)}
          />
        )}
      </article>
    </section>
  );
};

export default ProductHero;
